"use client";

import { X, Flag } from "lucide-react";
import { useState } from "react";

import { Button } from "@/src/components/shadcn/ui/button";
import { Card } from "@/src/components/shadcn/ui/card";
import { Label } from "@/src/components/shadcn/ui/label";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/src/components/shadcn/ui/tooltip";
import { EntityMatcher } from "@/src/components/tasks/intake-sections/matchers";
import type { DiagnosisItem } from "@/src/models/documents/referral";
import type { ICD10 } from "@/src/models/icd10";
import { formatICD10CodeForDisplay, normalizeICD10Code } from "@/src/models/icd10";

interface ICD10DiagnosisFormProps {
  items: DiagnosisItem[];
  onChange: (items: DiagnosisItem[]) => void;
  label?: string;
  isRequired?: boolean;
  fieldKey?: string;
  doesConflict?: boolean;
  onFlagForInaccuracyUpdate?: (key: string, value: any) => void;
  metadata?: any;
  setMetadata?: (metadata: any) => void;
  clearMetadata?: () => void;
}

export default function ICD10DiagnosisForm({
  items = [],
  onChange,
  label = "Diagnosis List",
  isRequired: _isRequired = false,
  fieldKey,
  doesConflict,
  onFlagForInaccuracyUpdate,
  metadata,
  setMetadata,
  clearMetadata,
}: ICD10DiagnosisFormProps) {
  const [showActionButtons, setShowActionButtons] = useState(false);

  const triggerChange = (newItems: DiagnosisItem[]) => {
    setShowActionButtons(true);
    onChange(newItems);
  };

  const handleSave = () => {
    setShowActionButtons(false);
    onFlagForInaccuracyUpdate?.(fieldKey || "", items);
  };

  const handleICD10Select = (icd10: ICD10 | null) => {
    if (!icd10) return;

    const newItem: DiagnosisItem = {
      diagnosisCode: icd10.code,
      diagnosisText: icd10.description,
      diagnosisCodeType: "ICD-10",
    };

    const normalizedNewCode = normalizeICD10Code(newItem.diagnosisCode);
    const isDuplicate = items.some((item) => normalizeICD10Code(item.diagnosisCode) === normalizedNewCode);

    if (!isDuplicate) {
      triggerChange([...items, newItem]);
    }
  };

  const handleRemoveItem = (index: number) => {
    const itemToRemove = items[index];
    const updatedItems = items.filter(
      (item) =>
        normalizeICD10Code(item.diagnosisCode) !== normalizeICD10Code(itemToRemove.diagnosisCode) ||
        item.diagnosisText !== itemToRemove.diagnosisText
    );

    triggerChange(updatedItems);
  };

  const getDisplayValue = (item: DiagnosisItem) => {
    const formattedCode = formatICD10CodeForDisplay(item.diagnosisCode);

    return `${formattedCode} ${item.diagnosisText || ""}`.trim();
  };

  return (
    <div className="px-1">
      {label && (
        <div className="flex items-center justify-between">
          <Label className="text-muted-foreground">{label}</Label>
          {onFlagForInaccuracyUpdate && showActionButtons && (
            <TooltipProvider>
              <Tooltip delayDuration={2000}>
                <TooltipTrigger asChild>
                  <Flag
                    className="h-3 w-3 text-orange-500 hover:text-orange-700"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSave();
                    }}
                  />
                </TooltipTrigger>
                <TooltipContent>
                  <span>Flag for AI extraction inaccuracy</span>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
      )}
      <Card
        className={`mt-2 p-4 w-full border-muted/80 rounded-sm shadow-sm ${doesConflict ? "bg-muted/10" : "bg-muted/5"}`}
        style={{ borderColor: doesConflict ? "orange" : "" }}
      >
        <div className="flex justify-center items-center mb-4">
          <div className="w-full">
            <EntityMatcher
              type="icd10"
              existingEntities={[]}
              isLoadingExistingEntities={false}
              entity={null}
              onEntitySelected={handleICD10Select}
              showCreateNewOption={false}
            />
          </div>
        </div>

        <ul className={`space-y-2 ${doesConflict ? "text-muted-foreground" : ""}`}>
          {items.map((item, index) => (
            <li key={index} className="space-y-2">
              <div className="flex space-x-2 items-center">
                <span
                  className="flex-grow text-sm overflow-hidden text-ellipsis"
                  tabIndex={0}
                  onFocus={() => {
                    if (setMetadata && metadata?.diagnosisList?.[index] !== undefined) {
                      const itemMetadata = metadata.diagnosisList[index];
                      const metadataToUse = itemMetadata.diagnosisCode_metadata || itemMetadata.diagnosisText_metadata;

                      if (metadataToUse) {
                        setMetadata(metadataToUse);
                      }
                    }
                  }}
                  onBlur={() => {
                    if (clearMetadata) {
                      clearMetadata();
                    }
                  }}
                >
                  {getDisplayValue(item)}
                </span>
                <div className="flex shrink-0 ml-auto">
                  <Button size="icon" variant="ghost" onClick={() => handleRemoveItem(index)}>
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </li>
          ))}
        </ul>

        {doesConflict && (
          <div className="flex items-center mt-3 text-xs text-muted-foreground justify-between">
            <span className="ml-1 pl-1">
              {doesConflict ? "Using matched data" : "Showing document data (matched data available)"}
            </span>
          </div>
        )}
      </Card>
    </div>
  );
}
